/**
 * URL pathname 정규화 유틸 (순수 함수).
 * Pathname normalization utilities (pure functions).
 *
 * TRAD-2102 — 운영 인프라가 `/board/idea` → `/board/idea/` 로 끝 슬래시 정규화
 * 리다이렉트를 하기 때문에, 라우트 상수(`/board/idea`)와 `pathname` 을 `===` 로
 * 직접 비교하면 운영에서만 false 가 되어 UI 가 조용히 사라진다.
 * 경로 비교는 반드시 이 헬퍼를 경유한다.
 *
 * Production infra redirects `/board/idea` to `/board/idea/` (trailing-slash
 * normalization), so comparing `pathname` to a route constant with `===`
 * silently evaluates to false in production only. Always compare through here.
 */

/**
 * 끝 슬래시를 제거한 정규 형태를 돌려준다. 루트('/')는 그대로 유지한다.
 * Strips trailing slashes; the root path stays '/'.
 */
export const normalizePathname = (pathname: string): string => {
  if (!pathname) return '/';
  const withoutTrailingSlash = pathname.replace(/\/+$/, '');
  return withoutTrailingSlash === '' ? '/' : withoutTrailingSlash;
};

/**
 * 끝 슬래시 유무와 무관하게 두 경로가 같은지 비교한다.
 * Trailing-slash tolerant pathname equality.
 */
export const isSamePathname = (pathname: string, target: string): boolean =>
  normalizePathname(pathname) === normalizePathname(target);

/**
 * 주어진 경로가 후보 목록 중 하나와 (끝 슬래시 무관하게) 일치하는지 판별한다.
 * True when pathname matches any of the candidates, ignoring trailing slashes.
 */
export const matchesAnyPathname = (
  pathname: string,
  targets: readonly string[],
): boolean => targets.some((target) => isSamePathname(pathname, target));
